import * as PIXI from 'pixi.js';
import isEqual from 'lodash/isEqual';

import { assertArray, assertMinimumLength, assertNumberBetween } from '../../validators';
import { combineRect, getRectFromPoints } from '../../libs/padding';
import type { Point } from '../../types';
import { StencilMask } from './StencilMask';

type PolygonMaskMorphState = {
    startPoints: number[];
    endPoints: number[];
};

type PointType = typeof PolygonMask.START_POINTS | typeof PolygonMask.END_POINTS;
/**
 * The PolygonMask Object will create a mask for an arbitrary shape, defined by an array of point coordinates.
 */
export class PolygonMask extends StencilMask {
    override _classType = 'PolygonMask';

    static readonly START_POINTS = 'startPoints';
    static readonly END_POINTS = 'endPoints';

    #startPoints: number[] = [0.9, 0.1, 0.9, 0.9, 0.1, 0.9, 0.1, 0.1];
    #endPoints: number[] = [0.9, 0.1, 0.9, 0.9, 0.1, 0.9, 0.1, 0.1];
    #overlayPointType: PointType = PolygonMask.START_POINTS;
    #overlaySelectedPointIndex: number | null = null;

    #morphState: PolygonMaskMorphState | null = null;

    /**
     * An array representing x/y coordinates of the points that define the object's shape at it's starting state.
     * e.g. [x1, y1, x2, y2, x3, y3 ... ]
     */
    get startPoints(): number[] {
        return this.#startPoints;
    }

    set startPoints(points: number[]) {
        if (isEqual(this.startPoints, points)) {
            return;
        }
        assertArray(points);
        assertMinimumLength(points, 2);
        this.#startPoints = points;
        this.requiresUpdate = true;
    }

    /**
     * An array representing x/y coordinates of the points that define the object's shape at it's ending state.
     * e.g. [x1, y1, x2, y2, x3, y3 ... ]
     */
    get endPoints(): number[] {
        return this.#endPoints;
    }

    set endPoints(points: number[]) {
        if (isEqual(this.endPoints, points)) {
            return;
        }
        assertArray(points);
        assertMinimumLength(points, 2);
        this.#endPoints = points;
        this.requiresUpdate = true;
    }

    setOverlaySelectedPoint(pointType: PointType, pointIndex: number | null) {
        if (
            this.#overlayPointType === pointType &&
            this.#overlaySelectedPointIndex === pointIndex
        ) {
            return;
        }
        this.#overlayPointType = pointType;
        this.#overlaySelectedPointIndex = pointIndex;
        this.requiresUpdate = true;
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat(['startPoints', 'endPoints']);
    }

    override getMaxBounds(): PIXI.Rectangle {
        const startRect = getRectFromPoints(this.#startPoints, this.root.referenceResolution);
        const endRect = getRectFromPoints(this.#endPoints, this.root.referenceResolution);
        const combined = combineRect([startRect, endRect]);
        return new PIXI.Rectangle(combined.x, combined.y, combined.width, combined.height);
    }

    /**
     * Draw the polygon shape, using the transition value to specify the point between the shape's start and end x/y
     * coordinate states.
     */
    override updateForRender() {
        super.updateForRender();
        const startPoints = this.#morphState?.startPoints ?? this.#startPoints;
        const endPoints = this.#morphState?.endPoints ?? this.#endPoints;

        const points = startPoints.map((_, i) => {
            let value = startPoints[i] * (1 - this.transition);
            value += endPoints[i] * this.transition;
            // TODO: bake down start and end points so they are no longer dependent on root
            value *=
                i % 2 ? this.root.referenceResolution.height : this.root.referenceResolution.width;
            value += i % 2 ? this.y : this.x;
            return value;
        });

        this.clear();
        this.beginFill(0xffffff, 1);
        this.drawPolygon(new PIXI.Polygon(points));
        this.endFill();
    }

    override createHelpers(): PIXI.DisplayObject[] {
        const helpers = super.createHelpers();

        const overlay = new PIXI.Graphics();
        overlay.name = 'PolygonMaskPointsOverlay';

        const circleRadius = 10;
        const selectedCircleRadius = 15;
        const selectedColor = 0xffff00;

        const drawPoints = (points: number[], color: number, selectedPointInd: number | null) => {
            overlay.beginFill(color, 1);
            for (let i = 0; i < points.length; i += 2) {
                const isSelected = selectedPointInd === i / 2;
                if (isSelected) {
                    overlay.endFill();
                    overlay.beginFill(selectedColor, 1);
                }
                const x = points[i] * this.root.referenceResolution.width + this.x;
                const y = points[i + 1] * this.root.referenceResolution.height + this.y;
                overlay.drawCircle(x, y, isSelected ? selectedCircleRadius : circleRadius);
                if (isSelected) {
                    overlay.endFill();
                    overlay.beginFill(color, 1);
                }
            }
            overlay.endFill();
        };

        if (this.#overlayPointType === PolygonMask.START_POINTS) {
            drawPoints(this.#endPoints, 0x0000ff, -1);
            drawPoints(this.#startPoints, 0xff0000, this.#overlaySelectedPointIndex);
        } else {
            drawPoints(this.#startPoints, 0xff0000, -1);
            drawPoints(this.#endPoints, 0x0000ff, this.#overlaySelectedPointIndex);
        }

        helpers.push(overlay);
        return helpers;
    }

    /**
     * @internal
     */
    _setMorphState(state: PolygonMaskMorphState): void {
        this.#morphState = state;
        this.requiresUpdate = true;
    }

    /**
     * @internal
     */
    _clearMorphState(): void {
        this.#morphState = null;
        this.requiresUpdate = true;
    }

    // -------------------------------------------------------------------------
    // Point Editing API
    //
    // The API deals with an array of {x, y} points not the flat arrays of
    // this.startPoints and this.endPoints, so that it is simpler to reason
    // about editing tasks.
    //
    // Arrays are considered copy-on-write, and setting any point to an array
    // creates a new array. This means this.startPoints and this.endPoints are
    // set to new arrays.
    //
    // The operations need to identify the array that is being worked on, using
    // PolygonMask.START_POINTS, PolygonMask.END_POINTS constants.
    // -------------------------------------------------------------------------

    /**
     * Get the points for a specific array.
     *
     * @param arrayId - PolygonMask.START_POINTS or PolygonMask.END_POINTS.
     */
    getPoints(arrayId: string): Point[] {
        switch (arrayId) {
            case PolygonMask.START_POINTS:
                return toPointArray(this.startPoints);
            case PolygonMask.END_POINTS:
                return toPointArray(this.endPoints);
            default:
                throw Error(`Unknown point array '${arrayId}'`);
        }
    }

    /**
     * Set the points array for a specific array.
     *
     * This method replaces either this.startPoints or this.endPoints.
     *
     * @param arrayId - PolygonMask.START_POINTS or PolygonMask.END_POINTS.
     * @param array - new points array value
     */
    setPoints(pointType: PointType, array: Point[]) {
        assertArray(array);

        switch (pointType) {
            case PolygonMask.START_POINTS:
                this.startPoints = toFlatArray(array);
                break;
            case PolygonMask.END_POINTS:
                this.endPoints = toFlatArray(array);
                break;
            default:
                throw Error(`Unknown point array '${pointType}'`);
        }
    }

    /**
     * Sets a point in the identified array.
     *
     * @param pointType PolygonMask.START_POINTS or PolygonMask.END_POINTS.
     * @param index Set the point at this index, overwriting the existing point.
     * @param point The point x, y to set.
     */
    setPoint(pointType: PointType, index: number, point: Point) {
        assertNumberBetween(index, 0, (this.startPoints.length >> 1) - 1);

        switch (pointType) {
            case PolygonMask.START_POINTS:
                {
                    const points = toPointArray(this.startPoints);
                    points[index] = point;
                    this.startPoints = toFlatArray(points);
                }
                break;
            case PolygonMask.END_POINTS:
                {
                    const points = toPointArray(this.endPoints);
                    points[index] = point;
                    this.endPoints = toFlatArray(points);
                }
                break;
            default:
                throw Error(`Unknown point array '${pointType}'`);
        }
    }

    /**
     * Get a point in the identified array.
     *
     * @param pointType PolygonMask.START_POINTS or PolygonMask.END_POINTS.
     * @param index Get the point at this index.
     */
    getPoint(pointType: PointType, index: number): Point {
        assertNumberBetween(index, 0, (this.startPoints.length >> 1) - 1);

        const flatIndex = index << 1;
        let pointArray;

        switch (pointType) {
            case PolygonMask.START_POINTS:
                pointArray = this.startPoints;
                break;
            case PolygonMask.END_POINTS:
                pointArray = this.endPoints;
                break;
            default:
                throw Error(`Unknown point array '${pointType}'`);
        }

        return { x: pointArray[flatIndex], y: pointArray[flatIndex + 1] };
    }

    /**
     * Inserts a point into the point arrays, moving the items at the index forward by one, i.e. the existing
     * point at index i is at i+1 after insertion.
     *
     * The new point will be at the mid point of its neighbours.
     *
     * The point arrays are required to contain the same number of points. Inserting a point inserts the point
     * into all the point arrays.
     *
     * @param index Insert the point before the value at this index.
     */
    insertPoint(index: number) {
        assertNumberBetween(index, 0, this.startPoints.length >> 1);
        assertNumberBetween(index, 0, this.endPoints.length >> 1);

        // Because -1 % 5 = -1, and want 4.
        const mod = (a: number, b: number) => {
            const r = a % b;
            return r < 0 ? r + b : r;
        };

        type Point = { x: number; y: number };

        // Makes a point at the mid point of two points.
        const midPoint = (array: Point[], index: number) => {
            const i1 = mod(index, array.length);
            const i2 = mod(index + 1, array.length);
            return {
                x: (array[i1].x + array[i2].x) / 2,
                y: (array[i1].y + array[i2].y) / 2,
            };
        };

        const insert = (array: Point[], index: number, value: Point) => {
            array.splice(index, 0, value);
            return array;
        };

        const array1 = toPointArray(this.startPoints);
        const array2 = toPointArray(this.endPoints);

        // Insertion is before the item at the index, therefore index - 1.
        this.startPoints = toFlatArray(insert(array1, index, midPoint(array1, index - 1)));
        this.endPoints = toFlatArray(insert(array2, index, midPoint(array2, index - 1)));
    }

    /**
     * Removes a point from the point arrays.
     *
     * The point arrays are required to contain the same number of points. Removing a point removes the point
     * from all the point arrays.
     *
     * @param index Remove the point at this index.
     */
    removePoint(index: number) {
        assertNumberBetween(index, 0, (this.startPoints.length >> 1) - 1);
        assertNumberBetween(index, 0, (this.endPoints.length >> 1) - 1);

        const remove = (array: { x: number; y: number }[], index: number) => {
            array.splice(index, 1);
            return array;
        };

        this.startPoints = toFlatArray(remove(toPointArray(this.startPoints), index));
        this.endPoints = toFlatArray(remove(toPointArray(this.endPoints), index));
    }

    /**
     * Moves a point from an index to another index.
     *
     * @param fromIndex Move the point from this index.
     * @param toIndex Move the point to this index.
     */
    movePoint(fromIndex: number, toIndex: number) {
        assertNumberBetween(fromIndex, 0, (this.startPoints.length >> 1) - 1);
        assertNumberBetween(toIndex, 0, (this.startPoints.length >> 1) - 1);
        assertNumberBetween(fromIndex, 0, (this.endPoints.length >> 1) - 1);
        assertNumberBetween(toIndex, 0, (this.endPoints.length >> 1) - 1);

        const move = (array: { x: number; y: number }[], fromIndex: number, toIndex: number) => {
            const point = array[fromIndex];
            array.splice(fromIndex, 1);
            array.splice(toIndex, 0, point);
            return array;
        };

        this.startPoints = toFlatArray(move(toPointArray(this.startPoints), fromIndex, toIndex));
        this.endPoints = toFlatArray(move(toPointArray(this.endPoints), fromIndex, toIndex));
    }
}

/**
 * Given a flat array, return a point array:
 * [1, 2, 3, 4] => [{ x: 1, y: 2 }, { x: 3, y: 4 }].
 *
 * @param array list of numbers
 */
function toPointArray(array: number[]) {
    const result = [];

    for (let i = 0; i < array.length; i += 2) {
        result.push({ x: array[i], y: array[i + 1] });
    }

    return result;
}

/**
 * Given a point array, return a flat array:
 * [{ x: 1, y: 2 }, { x: 3, y: 4 }] => [1, 2, 3, 4].
 *
 * @param array array of points
 */
function toFlatArray(array: Point[]) {
    return array.reduce((acc, point) => {
        acc.push(point.x, point.y);
        return acc;
    }, []);
}
